// Server functions for Drawing operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import type { Prisma } from '@/prisma/client/client'
import { z } from 'zod'
// Use shared types for typing, lightweight API contracts defined locally to avoid bundling heavy runtime schemas
import type { Drawing } from '@/shared/types/drawing'
import { generateShareId } from '@/src/server/lib/share'
import { aiQuality } from '@/src/lib/aiHelpers'
import { auth } from '@/auth'
import { assertDrawingAccess, buildAccessContext, drawingHasUserLink, getDrawingOrganizationId, isAdmin } from '@/src/server/lib/access'
import { isUuid } from '@/src/lib/uuid'
import { checkRateLimit, recordRateLimitEvent, getUserIdentifier, extractAnonIdFromRequest } from '@/src/server/lib/rateLimit'
import { hasPermission } from '@/src/server/lib/permissions'
import { hasPrivacyAccess } from '@/src/server/lib/privacyRules'
import { 
  buildDrawingWhere, 
  canAccessDrawing,
  getSessionFromRequest as getSession,
  requireUserId,
  requireAdmin as requireAdminAccess
} from '@/src/server/lib/accessControl'
import {
  logResourceCreate,
  logResourceAccess,
  logResourceUpdate,
  logResourceDelete,
  logUnauthorizedAccess,
  logRateLimitExceeded,
  logAdminAction
} from '@/src/server/lib/securityMonitoring'
import { getDrawingsForList, countDrawings } from '@/src/server/lib/queries/drawingQueries'
import { getThumbnailUrls } from '@/src/lib/thumbnails'
import { addDrawingsToProject, removeDrawingsFromProject } from '@/src/server/lib/projectDrawings'

// --- Create Drawing ---
// Lightweight API contract for create/upsert - avoid importing full AI schema here.
const DrawingCreateDataSchema = z.object({
  name: z.string().optional(),
  number: z.string().optional(),
  company: z.string().optional(),
  unitSystem: z.enum(['metric', 'imperial']).optional(),
  language: z.string().optional(),
  general: z.array(z.string()).optional(),
}).partial();

const CreateDrawingInput = z.object({
  data: DrawingCreateDataSchema,
  fileIds: z.array(z.string()),
  projectIds: z.array(z.string()).optional(),
})

export const createDrawing = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CreateDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    let userId: string | undefined = session?.user?.id
    
    if (userId) {
      const userExists = await db.user.findUnique({ where: { id: userId } })
      if (!userExists) userId = undefined
    }
    
    // Check rate limit for anonymous users
    if (!userId) {
      const request = getRequest()
      const anonId = request ? extractAnonIdFromRequest(request) : undefined
      const identifier = getUserIdentifier(session, anonId)
      
      const rateLimitCheck = await checkRateLimit(identifier, 'createDrawing')
      
      if (!rateLimitCheck.allowed) {
        return {
          data: data.data,
          error: { 
            code: 'RATE_LIMIT_EXCEEDED' as const, 
            message: `Rate limit exceeded. You can create ${rateLimitCheck.remaining} more drawings. Try again after ${rateLimitCheck.resetAt.toLocaleTimeString()}.` 
          },
        }
      }
      
      // Record the rate limit event
      await recordRateLimitEvent(identifier, 'createDrawing')
    }
    
    try {
      // Create or get DrawingMaster for the first file
      const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
      const masterResult = await getOrCreateDrawingMaster(data.fileIds[0])
      
      const drawing = await db.drawing.create({
        data: {
          shareId: await generateShareId({
            prefix: 'D',
            length: 8,
            isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
          }),
          name: data.data.name ?? '',
          number: data.data.number ?? '',
          privacy: 'LOW',
          masterId: masterResult.master.id,
          // Note: content field removed - extracted data now stored in DrawingMaster
          files: { connect: data.fileIds.map(id => ({ id })) },
          ...(userId ? { userId } : {}),
        },
      })
      
      // Log the drawing creation with security monitoring
      await logResourceCreate(
        userId || 'anonymous',
        'drawing',
        drawing.id,
        {
          drawingName: drawing.name,
          drawingNumber: drawing.number,
          fileCount: data.fileIds.length,
        }
      )
      
      // Cache invalidation disabled - Prisma Accelerate not configured
      // await db.$accelerate.invalidate({ tags: ['drawingList', 'drawingListAll', 'drawingListByOrgId'] })
      
      return {
        data: data.data,
        error: undefined,
      }
    } catch (e: any) {
      return {
        data: data.data,
        error: { code: 'INTERNAL_ERROR' as const, message: e?.message || 'Failed to create drawing.' },
      }
    }
  })

// --- Get Drawing by ID ---
const GetDrawingInput = z.object({
  id: z.string(),
})

export const getDrawing = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const drawing = await (db as any).drawing.findUnique({
      where: { id: data.id },
      include: { 
        files: { include: { userFiles: { where: { deletedAt: null }, include: { user: true } } } },
        master: true,
      },
    })
    
    if (!drawing) {
      throw new Error(`Drawing with id '${data.id}' not found.`)
    }
    
    await assertDrawingAccess(drawing, session)

    // Get effective data (merged master + overrides)
    const { getEffectiveDrawingData } = await import('@/src/server/lib/drawingMerge')
    const effectiveData = getEffectiveDrawingData(drawing)

    return {
      ...drawing,
      effectiveData, // Add merged data to response
      files: drawing.files.map((file: any) => {
        const thumbnails = getThumbnailUrls(file.id)
        return {
          id: file.id,
          name: file.name,
          createdAt: file.createdAt instanceof Date ? file.createdAt.toISOString() : file.createdAt,
          shareId: file.shareId ?? '',
          hash: file.hash ?? '',
          thumbnailSmallUrl: thumbnails.small,
          thumbnailLargeUrl: thumbnails.large,
        }
      }),
      createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
    }
  })

// --- Get Drawing by Share ID ---
const GetDrawingByShareIdInput = z.object({
  shareId: z.string(),
})

export const getDrawingByShareId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetDrawingByShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    
    // Support both shareId and database ID
    // UUID format: 8-4-4-4-12 (with hyphens)
    // ShareId format: D followed by alphanumeric (no hyphens)
    const isShareIdLookup = !isUuid(data.shareId)
    const whereClause = isShareIdLookup
      ? { shareId: data.shareId }
      : { id: data.shareId }
    
    const drawing = await (db as any).drawing.findFirst({
      where: whereClause,
      include: { 
        files: { include: { userFiles: { where: { deletedAt: null }, include: { user: true } } } },
        master: true,
      },
    })
    
    if (!drawing) return null
    
    // SECURITY: ShareID access bypasses privacy checks - having the shareId IS the authorization.
    // For direct ID access, enforce privacy-based access control.
    if (!isShareIdLookup) {
      try {
        await assertDrawingAccess(drawing, session)
      } catch {
        return null
      }
    }
    
    // Get effective data (merged master + overrides)
    const { getEffectiveDrawingData } = await import('@/src/server/lib/drawingMerge')
    const effectiveData = getEffectiveDrawingData(drawing)

    return {
      ...drawing,
      effectiveData, // Add merged data to response
      // Map error to errorMessage for consistency with other APIs
      errorMessage: drawing.error,
      files: drawing.files.map((file: any) => {
        const thumbnails = getThumbnailUrls(file.id)
        return {
          id: file.id,
          name: file.name,
          createdAt: file.createdAt instanceof Date ? file.createdAt.toISOString() : file.createdAt,
          shareId: file.shareId ?? '',
          hash: file.hash ?? '',
          thumbnailSmallUrl: thumbnails.small,
          thumbnailLargeUrl: thumbnails.large,
        }
      }),
      createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
    }
  })

// --- List Drawings ---
// If all=true, returns all drawings (admin), otherwise applies privacy filtering
const ListDrawingsInput = z.object({
  all: z.boolean().optional(),
  projectId: z.string().optional(),
  fileId: z.string().optional(),
  userId: z.string().optional(),
  search: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  orderBy: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
}).optional()

export const listDrawings = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListDrawingsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    const input = data
    
    const buildDrawingOrderBy = (orderBy?: string, order?: 'asc' | 'desc') => {
      const direction = order || 'desc'
      switch (orderBy) {
        case 'name':
          return { name: direction }
        case 'number':
          return { number: direction }
        case 'company':
          return { company: direction } // Now a direct column
        case 'privacy':
          return { privacy: direction }
        case 'status':
          return { status: direction }
        case 'createdAt':
          return { createdAt: direction }
        case 'completedAt':
          return { completedAt: direction }
        case 'user':
          return { user: { name: direction } }
        default:
          return { createdAt: direction }
      }
    }

    // Admin mode - no privacy filtering (REQUIRES ADMIN or DRAWINGS_LIST_ALL for org)
    if (input?.all) {
      // SECURITY: Check permission to list all drawings
      const isUserAdmin = await isAdmin(session)
      const canListAllDrawings = await import('@/src/server/lib/permissions').then(m => 
        m.hasPermission(session, ['DRAWINGS_LIST_ALL', 'ADMIN'])
      )
      
      if (!canListAllDrawings) {
        await logUnauthorizedAccess(userId, 'drawing', undefined, {
          action: 'list all drawings',
          attemptedAdminMode: true,
        })
        throw new Error('Unauthorized: Admin or DRAWINGS_LIST_ALL permission required for all=true mode')
      }
      
      // Log admin action
      await logAdminAction(userId!, 'List all drawings', {
        filters: input,
      })
      
      // Build base conditions
      const andConditions: any[] = []
      
      // If not global admin, filter by organization
      if (!isUserAdmin && userId) {
        // Org owner: only see drawings from users in their organization
        const user = await db.user.findUnique({
          where: { id: userId },
          select: { organizationId: true },
        })
        
        if (user?.organizationId) {
          // Get all users in the organization
          const orgUsers = await db.user.findMany({
            where: { organizationId: user.organizationId },
            select: { id: true },
          })
          const orgUserIds = orgUsers.map(u => u.id)
          
          // Filter to drawings created by organization members
          andConditions.push({
            userId: { in: orgUserIds },
          })
        } else {
          // No organization - shouldn't have DRAWINGS_LIST_ALL permission
          throw new Error('Unauthorized')
        }
      }
      
      // Apply additional filters
      if (input?.projectId) andConditions.push({ projects: { some: { id: input.projectId } } })
      if (input?.fileId) andConditions.push({ files: { some: { id: input.fileId } } })
      if (input?.userId) andConditions.push({ userId: input.userId })
      if (input?.search) {
        andConditions.push({
          OR: [
            { name: { contains: input.search, mode: 'insensitive' } },
            { number: { contains: input.search, mode: 'insensitive' } },
            { company: { contains: input.search, mode: 'insensitive' } },
            { projects: { some: { name: { contains: input.search, mode: 'insensitive' } } } },
          ],
        })
      }
      
      const where: any = andConditions.length > 0 ? { AND: andConditions } : {}
      
      const orderBy = buildDrawingOrderBy(input?.orderBy, input?.order)
      
      const [drawings, total] = await Promise.all([
        getDrawingsForList({
          where,
          orderBy: orderBy || { createdAt: 'desc' },
          skip: input?.offset ?? 0,
          take: input?.limit ?? 20,
        }),
        countDrawings(where),
      ])
      
      return { drawings, total }
    }
    
    // Regular mode - with privacy filtering
    // SECURITY: Use centralized access control utility
    const isUserAdmin = await isAdmin(session)
    const baseWhere = buildDrawingWhere(userId, isUserAdmin, { 
      allowPublic: false,
      requireOwnership: true 
    })
    
    // Add additional filters
    const where: any = {
      AND: [
        baseWhere,
        input?.projectId
          ? { projects: { some: { id: input.projectId } } }
          : undefined,
        input?.fileId
          ? { files: { some: { id: input.fileId } } }
          : undefined,
        input?.search
          ? {
              OR: [
                { name: { contains: input.search, mode: 'insensitive' } },
                { number: { contains: input.search, mode: 'insensitive' } },
                { company: { contains: input.search, mode: 'insensitive' } },
                { projects: { some: { name: { contains: input.search, mode: 'insensitive' } } } },
              ],
            }
          : undefined,
      ].filter(Boolean),
    }
    
    const orderBy = buildDrawingOrderBy(input?.orderBy || 'createdAt', input?.order || 'desc')
    
    const [drawings, total] = await Promise.all([
      getDrawingsForList({
        where,
        orderBy: orderBy || { createdAt: 'desc' },
        skip: input?.offset ?? 0,
        take: input?.limit ?? 100,
      }),
      countDrawings(where),
    ])
    
    return { drawings, total }
  })

// --- Upsert Drawing ---
const UpsertDrawingInput = z.object({
  id: z.string().optional(),
  data: z.record(z.string(), z.any()),
  fileIds: z.array(z.string()),
  projectIds: z.array(z.string()).optional(),
})

export const upsertDrawing = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const { id, data: drawingData, fileIds, projectIds } = data
    
    const userId = requireUserId(session)
    
    let drawing
    if (id) {
      // Verify access to existing drawing
      const existingDrawing = await db.drawing.findUnique({
        where: { id },
        include: { master: true },
      })
      
      if (!existingDrawing) {
        await logUnauthorizedAccess(userId, 'drawing', id, { action: 'update', reason: 'not found' })
        throw new Error('Drawing not found')
      }
      
      await assertDrawingAccess(existingDrawing, session)
      
      // Update the drawing with overrides using the service
      if (existingDrawing.master && drawingData) {
        const { updateDrawingOverrides } = await import('@/src/server/lib/drawingMaster')
        drawing = await updateDrawingOverrides(id, drawingData)
      } else {
        // No master yet - just update metadata
        drawing = await db.drawing.update({
          where: { id },
          data: {
            name: drawingData.name,
            number: drawingData.number,
            files: { set: fileIds.map(id => ({ id })) },
          },
        })
      }
      
      // Log the drawing update with security monitoring
      await logResourceUpdate(userId, 'drawing', id, {
        drawingName: drawing.name,
        drawingNumber: drawing.number,
        hasOverrides: drawing.overrides !== null,
      })
    } else {
      // Create new drawing with DrawingMaster
      const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
      const masterResult = await getOrCreateDrawingMaster(fileIds[0])
      
      drawing = await db.drawing.create({
        data: {
          shareId: await generateShareId({
            prefix: 'D',
            length: 8,
            isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
          }),
          name: drawingData.name ?? '',
          number: drawingData.number ?? '',
          masterId: masterResult.master.id,
          files: { connect: fileIds.map(id => ({ id })) },
          userId,
        },
      })
      
      // Log the drawing creation with security monitoring
      await logResourceCreate(userId, 'drawing', drawing.id, {
        drawingName: drawing.name,
        drawingNumber: drawing.number,
        fileCount: fileIds.length,
      })
    }
    
    // Update project associations if provided
    if (projectIds && projectIds.length > 0) {
      // Use helper to maintain both relation and JSON consistency
      await Promise.all(
        projectIds.map(projectId => 
          addDrawingsToProject(projectId, [drawing.id])
        )
      )
    }
    
    return drawing
  })

// --- Get Recent Drawings (for job history on upload page) ---
const GetRecentDrawingsInput = z.object({
  limit: z.number().optional().default(5),
  onlyWithJobs: z.boolean().optional().default(false),
})

export const getRecentDrawings = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetRecentDrawingsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    
    // CRITICAL: Only return drawings for authenticated users
    // Prevent unauthorized access to other users' drawings
    if (!userId) {
      return []
    }
    
    const drawings = await (db as any).drawing.findMany({
      where: {
        userId,
        ...(data.onlyWithJobs ? { jobs: { some: {} } } : {}),
      },
      include: {
        files: {
          select: { id: true, name: true, type: true },
        },
      },
      orderBy: { createdAt: 'desc' },
      take: data.limit,
    })
    
    return drawings.map((drawing: any) => ({
      id: drawing.id,
      shareId: drawing.shareId,
      name: drawing.name,
      number: drawing.number,
      status: drawing.status,
      createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
      files: drawing.files.map((f: any) => ({
        id: f.id,
        name: f.name,
        type: f.type,
      })),
    }))
  })

// --- Get Drawings by IDs (for polling analysis status) ---
const GetDrawingsByIdsInput = z.object({
  ids: z.array(z.string()),
})

export const getDrawingsByIds = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetDrawingsByIdsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    const isUserAdmin = await isAdmin(session)
    
    // Note: Job processing is now handled by the job processor (background process)
    // No need to trigger processing here - job processor polls the Job table
    
    // SECURITY: Unauthenticated users cannot access drawings by raw database IDs.
    if (!userId && !isUserAdmin) {
      return []
    }

    // Fetch requested drawings, then enforce centralized access rules per drawing.
    const drawings = await (db as any).drawing.findMany({
      where: {
        id: { in: data.ids },
      },
      include: { 
        files: { 
          include: { 
            userFiles: { where: { deletedAt: null }, include: { user: true } } 
          } 
        },
        master: true,
      },
    })

    let accessibleDrawings = drawings
    if (!isUserAdmin) {
      const authenticatedUserId = userId as string
      const accessContext = await buildAccessContext(session)
      accessibleDrawings = drawings.filter((drawing: any) =>
        hasPrivacyAccess(accessContext, {
          ownerId: drawingHasUserLink(drawing, authenticatedUserId) ? authenticatedUserId : undefined,
          organizationId: getDrawingOrganizationId(drawing),
          privacy: drawing?.privacy || 'HIGH',
        })
      )
    }
    
    // Get effective data helper
    const { getEffectiveDrawingData } = await import('@/src/server/lib/drawingMerge')
    
return accessibleDrawings.map((drawing: any) => {
      // Compute effective data (merged master + overrides)
      const effectiveData = getEffectiveDrawingData(drawing)
      
      return {
        id: drawing.id,
        shareId: drawing.shareId,
        name: drawing.name,
        number: drawing.number,
        status: drawing.status,
        errorMessage: drawing.error,
        effectiveData, // Add merged data to response
        master: drawing.master ? {
          id: drawing.master.id,
          data: drawing.master.data,
          confidenceScore: drawing.master.confidenceScore,
          processingStatus: drawing.master.processingStatus,
        } : null,
        overrides: drawing.overrides,
        files: drawing.files.map((file: any) => ({
          id: file.id,
          name: file.name,
          shareId: file.shareId ?? '',
          hash: file.hash ?? '',
          createdAt: file.createdAt instanceof Date ? file.createdAt.toISOString() : file.createdAt,
        })),
        createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
      }
    })
  })

// --- Roll Drawing Share ID ---
const RollDrawingShareIdInput = z.object({
  drawingId: z.string(),
})

export const rollDrawingShareId = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RollDrawingShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    if (!session?.user?.id) throw new Error('Unauthorized.')

    const drawing = await db.drawing.findUnique({ where: { id: data.drawingId } })
    if (!drawing) throw new Error('Drawing not found.')

    const isOwner = !!drawing.userId && drawing.userId === session.user.id
    const isAdminUser = await isAdmin(session)
    if (!isOwner && !isAdminUser) throw new Error('Forbidden.')

    const newShareId = await generateShareId({
      prefix: 'D',
      length: 8,
      isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
    })

    await db.drawing.update({
      where: { id: data.drawingId },
      data: { shareId: newShareId },
    })

    // Cache invalidation disabled - Prisma Accelerate not configured
    // await db.$accelerate.invalidate({
    //   tags: ['drawingList', 'drawingListAll', 'drawingListByOrgId', 'drawingGet', 'drawingGetByShareId'],
    // })

    return { shareId: newShareId }
  })

// --- Delete Drawing (Soft delete for user) ---
const DeleteDrawingInput = z.object({
  drawingId: z.string(),
})

export const deleteDrawing = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    if (!session?.user?.id) throw new Error('Unauthorized.')

    const drawing = await db.drawing.findUnique({ where: { id: data.drawingId } })
    if (!drawing) throw new Error('Drawing not found.')

    const isOwner = !!drawing.userId && drawing.userId === session.user.id
    const isAdminUser = await isAdmin(session)
    
    // Allow owner, admin, or org admin with DRAWINGS_MANAGE permission for org drawings
    if (!isOwner && !isAdminUser) {
      // Check if user has org-level management permissions for this drawing
      const canManageOrgDrawings = await hasPermission(session, ['DRAWINGS_MANAGE'])
      if (canManageOrgDrawings && drawing.userId) {
        const [user, drawingOwner] = await Promise.all([
          db.user.findUnique({ where: { id: session.user.id }, select: { organizationId: true } }),
          db.user.findUnique({ where: { id: drawing.userId }, select: { organizationId: true } }),
        ])
        if (!user?.organizationId || user.organizationId !== drawingOwner?.organizationId) {
          throw new Error('Forbidden. Only the owner or organization admin can delete this drawing.')
        }
      } else {
        throw new Error('Forbidden. Only the owner can delete this drawing.')
      }
    }

    // Delete only this user's drawing (not the master)
    await db.drawing.delete({ where: { id: data.drawingId } })

    // Log the deletion
    db.logEntry.create({
      data: {
        type: 'DeleteDrawing',
        userId: session.user.id,
        details: {
          drawingId: drawing.id,
          drawingName: drawing.name,
          drawingNumber: drawing.number,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { success: true }
  })

// --- Delete Drawing Hard (Admin only) ---
export const deleteDrawingHard = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const isAdminUser = await isAdmin(session)
    
    if (!isAdminUser) throw new Error('Unauthorized. Admin access required.')

    const drawing = await db.drawing.findUnique({ 
      where: { id: data.drawingId },
      include: { master: true }
    })
    if (!drawing) throw new Error('Drawing not found.')
    if (!drawing.masterId) throw new Error('Drawing has no master.')

    // Delete the master (cascades to all user drawings)
    await db.drawingMaster.delete({ where: { id: drawing.masterId } })
    
    // Log the deletion
    db.logEntry.create({
      data: {
        type: 'DeleteDrawingMaster',
        userId: session?.user?.id,
        details: {
          masterId: drawing.masterId,
          drawingName: drawing.name,
          drawingNumber: drawing.number,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { success: true }
  })

// --- Assign Drawings to Project ---
const AssignDrawingsToProjectInput = z.object({
  drawingIds: z.array(z.string()),
  projectId: z.string().nullable(), // null to unassign
})

export const assignDrawingsToProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => AssignDrawingsToProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    
    if (!userId) throw new Error('Unauthorized.')

    // Verify user has access to the project (if assigning)
    if (data.projectId) {
      const project = await db.project.findUnique({ 
        where: { id: data.projectId },
        include: { users: true }
      })
      if (!project) throw new Error('Project not found.')
      
      const isOwner = project.createdById === userId
      const isLinked = project.users.some((u: any) => u.id === userId)
      const isAdminUser = await isAdmin(session)
      
      if (!isOwner && !isLinked && !isAdminUser) {
        throw new Error('Not authorized to modify this project.')
      }
    }

    // Verify user owns the drawings
    const drawings = await db.drawing.findMany({
      where: { id: { in: data.drawingIds } }
    })
    
    const isAdminUser = await isAdmin(session)
    for (const drawing of drawings) {
      if (drawing.userId !== userId && !isAdminUser) {
        throw new Error(`Not authorized to modify drawing: ${drawing.name || drawing.id}`)
      }
    }

    // First, remove drawings from ALL their current projects (to get their old projects)
    const drawingsWithProjects = await db.drawing.findMany({
      where: { id: { in: data.drawingIds } },
      include: { projects: { select: { id: true } } }
    })
    
    // Remove from old projects
    for (const drawing of drawingsWithProjects) {
      if (drawing.projects.length > 0) {
        await Promise.all(
          drawing.projects.map(p => 
            removeDrawingsFromProject(p.id, [drawing.id])
          )
        )
      }
    }
    
    // Add to new project if specified (using helper to maintain both relation and JSON)
    if (data.projectId) {
      await addDrawingsToProject(data.projectId, data.drawingIds)
    }

    // Log the assignment
    db.logEntry.create({
      data: {
        type: data.projectId ? 'AssignDrawingsToProject' : 'UnassignDrawingsFromProject',
        userId,
        details: {
          drawingIds: data.drawingIds,
          projectId: data.projectId,
          drawingCount: data.drawingIds.length,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { success: true, count: data.drawingIds.length }
  })

// --- List Unassigned Drawings ---
const ListUnassignedDrawingsInput = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
}).optional()

export const listUnassignedDrawings = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListUnassignedDrawingsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    
    if (!userId) throw new Error('Unauthorized.')

    const where = {
      userId,
      projects: { none: {} },  // No projects assigned
    }

    const [drawings, total] = await Promise.all([
      getDrawingsForList({
        where,
        orderBy: { createdAt: 'desc' },
        skip: data?.offset ?? 0,
        take: data?.limit ?? 50,
      }),
      countDrawings(where),
    ])

    return { drawings, total }
  })

// --- Create Manual Drawing (without file upload) ---
// This allows service technicians to create drawings with just data entry
// Uses the same DrawingDataSchema as AI-extracted drawings for consistency
import { DrawingDataSchema } from '@/shared/schemas/drawing-data.zod'
import { generateDrawingFingerprint } from '@/shared/lib/drawingFingerprint'

const CreateManualDrawingInput = z.object({
  data: DrawingDataSchema.extend({
    name: z.string().min(1, 'Name is required'),
  }),
  fileIds: z.array(z.string()).optional(), // Optional file attachments
  projectIds: z.array(z.string()).optional(),
})

export const createManualDrawing = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CreateManualDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    
    if (!userId) {
      throw new Error('Authentication required to create drawings manually')
    }
    
    // Verify user exists
    const userExists = await db.user.findUnique({ where: { id: userId } })
    if (!userExists) {
      throw new Error('User not found')
    }
    
    try {
      // For manual drawings, we create a DrawingMaster with the user-provided data
      // instead of AI-extracted data
      let masterId: string | null = null
      
      // If files are attached, create/get master for first file
      if (data.fileIds && data.fileIds.length > 0) {
        const { getOrCreateDrawingMaster } = await import('@/src/server/lib/drawingMaster')
        const masterResult = await getOrCreateDrawingMaster(data.fileIds[0])
        masterId = masterResult.master.id
      }
      
      // Use the provided data directly as overrides (same schema as AI-extracted)
      const drawingOverrides = data.data
      
      // Generate fingerprint for similarity search
      const fingerprint = generateDrawingFingerprint(data.data)
      
      const drawing = await db.drawing.create({
        data: {
          shareId: await generateShareId({
            prefix: 'D',
            length: 8,
            isIdAvailable: async (id) => !(await db.drawing.findUnique({ where: { shareId: id } })),
          }),
          name: data.data.name || '',
          number: data.data.number ?? '',
          company: data.data.company,
          privacy: 'LOW',
          status: 'COMPLETED', // Manual drawings are immediately complete
          ...(masterId && { masterId }),
          overrides: drawingOverrides,
          fingerprint, // Store fingerprint for similarity search
          ...(data.fileIds && data.fileIds.length > 0 && {
            files: { connect: data.fileIds.map(id => ({ id })) }
          }),
          userId,
        },
        include: {
          files: true,
        },
      })
      
      // Add to projects if specified
      if (data.projectIds && data.projectIds.length > 0) {
        await Promise.all(
          data.projectIds.map(projectId => 
            addDrawingsToProject(projectId, [drawing.id])
          )
        )
      }
      
      // Log the drawing creation
      await logResourceCreate(
        userId,
        'drawing',
        drawing.id,
        {
          drawingName: drawing.name,
          drawingNumber: drawing.number,
          isManual: true,
          fileCount: data.fileIds?.length ?? 0,
        }
      )
      
      return {
        id: drawing.id,
        shareId: drawing.shareId,
        name: drawing.name,
        number: drawing.number,
        status: drawing.status,
        createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
      }
    } catch (e: any) {
      console.error('Failed to create manual drawing:', e)
      throw new Error(e?.message || 'Failed to create drawing')
    }
  })

// --- Save Drawing Base Data (gear / tool base parameters) ---
// Accepts a partial DrawingData so callers only need to supply the relevant
// array (e.g. { gear: [{ numberOfTeeth: 20, … }] }).
// Handles drawings with a linked DrawingMaster (computes delta / stores overrides)
// and manually-created drawings that store overrides directly.
const SaveDrawingBaseDataInput = z.object({
  drawingId: z.string(),
  data: DrawingDataSchema.partial(),
})

export const saveDrawingBaseData = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => SaveDrawingBaseDataInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = requireUserId(session)

    const existingDrawing = await db.drawing.findUnique({
      where: { id: data.drawingId },
      include: { master: true },
    })

    if (!existingDrawing) {
      await logUnauthorizedAccess(userId, 'drawing', data.drawingId, {
        action: 'saveBaseData',
        reason: 'not found',
      })
      throw new Error('Drawing not found')
    }

    await assertDrawingAccess(existingDrawing, session)

    if (existingDrawing.master) {
      // Compute and store only the delta from AI-extracted master data
      const { updateDrawingOverrides } = await import('@/src/server/lib/drawingMaster')
      const updated = await updateDrawingOverrides(data.drawingId, data.data as any)
      await logResourceUpdate(userId, 'drawing', data.drawingId, { action: 'saveBaseData', hasMaster: true })
      return updated
    } else {
      // Manual drawing without a master – merge new base data directly into overrides
      const existing = (existingDrawing.overrides as Record<string, any>) ?? {}
      const incoming = data.data as Record<string, any>
      // Deep-merge arrays (gear, tool) from incoming into existing overrides
      const merged: Record<string, any> = { ...existing }
      for (const key of Object.keys(incoming)) {
        if (Array.isArray(incoming[key]) && Array.isArray(existing[key])) {
          // Replace first element, keep the rest (supports multi-gear future expansion)
          merged[key] = [{ ...existing[key][0], ...incoming[key][0] }, ...existing[key].slice(1)]
        } else {
          merged[key] = incoming[key]
        }
      }
      const updated = await db.drawing.update({
        where: { id: data.drawingId },
        data: {
          overrides: merged as any,
          name: (incoming as any).name ?? existingDrawing.name,
          number: (incoming as any).number ?? existingDrawing.number,
          company: (incoming as any).company ?? existingDrawing.company,
        },
      })
      await logResourceUpdate(userId, 'drawing', data.drawingId, { action: 'saveBaseData', hasMaster: false })
      return updated
    }
  })

// --- Search Drawings (basic drawing metadata field search) ---
// Direct fingerprint-based similarity matching lives in /api/gears/similar.

const SearchDrawingsInput = z.object({
  // Text search
  query: z.string().optional(),
  // Filter by company
  company: z.string().optional(),
  // Pagination
  limit: z.number().optional(),
  offset: z.number().optional(),
})

export const searchDrawings = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => SearchDrawingsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const userId = session?.user?.id
    const isUserAdmin = await isAdmin(session)

    // Build the where clause with faceted filtering (AND between categories, OR within)
    const andConditions: Prisma.DrawingWhereInput[] = []

    // Text search category: query can match name, number, or company
    if (data.query) {
      andConditions.push({
        OR: [
          { name: { contains: data.query, mode: 'insensitive' } },
          { number: { contains: data.query, mode: 'insensitive' } },
          { company: { contains: data.query, mode: 'insensitive' } }
        ]
      })
    }

    // Company filter category: must match if specified
    if (data.company) {
      andConditions.push({ company: { contains: data.company, mode: 'insensitive' } })
    }

    // SECURITY: Apply privacy filtering
    const baseWhere = buildDrawingWhere(userId, isUserAdmin, { 
      allowPublic: true,
      requireOwnership: false 
    })
    
    const where: any = {
      AND: [
        baseWhere,
        ...andConditions,
      ].filter(v => Object.keys(v).length > 0),
    }

    const effectiveWhere = where.AND?.length > 0 ? where : baseWhere;

    const [total, drawings] = await Promise.all([
      db.drawing.count({ where: effectiveWhere }),
      db.drawing.findMany({
        where: effectiveWhere,
        include: {
          master: true,
          files: {
            select: { id: true, name: true, shareId: true },
            take: 1,
          },
          user: {
            select: { id: true, name: true },
          },
        },
        orderBy: { createdAt: 'desc' },
        skip: data.offset ?? 0,
        take: data.limit ?? 50,
      }),
    ])

    type DrawingWithRelations = Awaited<ReturnType<typeof db.drawing.findMany>>[number] & {
      master: { data: any } | null
      files: { id: string; name: string; shareId: string }[]
      user: { id: string; name: string } | null
    }

    const mappedDrawings = drawings.map((drawing: DrawingWithRelations) => {
      const effectiveData = { ...(drawing.master?.data as any || {}), ...(drawing.overrides as any || {}) }
      const gear = effectiveData.gear?.[0]

      return {
        id: drawing.id,
        shareId: drawing.shareId,
        name: drawing.name || effectiveData.name,
        number: drawing.number || effectiveData.number,
        company: drawing.company || effectiveData.company,
        status: drawing.status,
        privacy: drawing.privacy,
        createdAt: drawing.createdAt instanceof Date ? drawing.createdAt.toISOString() : drawing.createdAt,
        gear: gear ? {
          numberOfTeeth: gear.numberOfTeeth,
          normalModule: gear.normalModule,
          normalPressureAngle: gear.normalPressureAngle,
          helixAngle: gear.helixAngle,
          type: gear.type,
        } : null,
        material: effectiveData.material?.material,
        file: drawing.files[0] || null,
        user: drawing.user,
      }
    })

    return {
      drawings: mappedDrawings,
      total,
    }
  })

// --- Regenerate Fingerprints (Admin only) ---
// This endpoint regenerates fingerprints for all drawings or a specific set
// Useful for normalization runs after schema changes
const RegenerateFingerprintsInput = z.object({
  drawingIds: z.array(z.string()).optional(), // If not provided, regenerates for all
  batchSize: z.number().optional().default(100),
})

export const regenerateFingerprints = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RegenerateFingerprintsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSession()
    const isUserAdmin = await isAdmin(session)
    
    if (!isUserAdmin) {
      throw new Error('Admin access required to regenerate fingerprints')
    }
    
    const userId = session?.user?.id
    if (!userId) {
      throw new Error('Authentication required')
    }
    
    // Get drawings to process
    const drawings = await db.drawing.findMany({
      where: data.drawingIds ? { id: { in: data.drawingIds } } : {},
      include: { master: true },
      take: data.batchSize,
    })
    
    let updated = 0
    
    for (const drawing of drawings) {
      const effectiveData = drawing.overrides || drawing.master?.data || {}
      const fingerprint = generateDrawingFingerprint(effectiveData)
      
      if (fingerprint !== drawing.fingerprint) {
        await db.drawing.update({
          where: { id: drawing.id },
          data: { fingerprint },
        })
        updated++
      }
    }
    
    await logAdminAction(
      userId,
      'regenerate_fingerprints',
      {
        requested: drawings.length,
        updated,
        drawingIds: data.drawingIds,
      }
    )
    
    return {
      processed: drawings.length,
      updated,
    }
  })
